// The message contract between a framed presentation (a story page whose
// root is <pt-deck>, run by webviz lib/deck.js) and the Platinum page that
// frames it. Version 1, same origin only. See
// design/2026-11-03-presentations.md "Message contract".
//
// Page -> host: { source: 'platinum-deck', v: 1, type, ... }
//   ready    { title, slides: [{ id, title }] }
//   location { at: '#/<slide>/<layer>...' }
//   ask      { at, kind: 'slide' | 'layer' | 'figure', path: [titles], figure? }
// Host -> page: { source: 'platinum-host', v: 1, type, ... }
//   ask-state { enabled, reason? }
//   go        { at }
//
// The page is written by an agent, so anything it posts is untrusted:
// - a message counts only when it comes from the framed iframe's own window
//   and from this origin;
// - ids must match the story lint's id rule;
// - labels (titles, the breadcrumb, a figure's text) are for display only,
//   cleaned and length-capped here;
// - the host names the source (artifact or dashboard id) itself and never
//   reads one from a message;
// - a message opens a dialog or prefills a composer. Nothing here sends.
//
// Pages published before decks existed never send `ready`, and the host
// stays silent for them: no ask-state, no go.

export const DECK_SOURCE = 'platinum-deck'
export const HOST_SOURCE = 'platinum-host'
export const FRAME_VERSION = 1

/** "Start a new chat from here" while a web app has no saved row (no id) yet. */
export const PAGE_NOT_SAVED_REASON = 'This story board is still saving. Try again in a moment.'

/** The story lint's id rule for pt-slide and pt-layer. */
export const DECK_ID_RE = /^[a-z][a-z0-9-]{0,63}$/
/** A dataset name, as the seed route checks it. */
export const DECK_DATASET_RE = /^[A-Za-z0-9_.-]{1,128}$/
/** Layers nest at most three deep (story lint); the path of titles names each one. */
export const DECK_MAX_LAYERS = 3

const TITLE_MAX = 160
const FIGURE_TEXT_MAX = 200
/** A starting question: plain text, at most this long (deck.js sends no more). */
export const DECK_QUESTION_MAX = 300
/** The seed route's bound on a cell index. */
export const DECK_MAX_CELL_INDEX = 100000
const MAX_SLIDES = 200

/** Where a deck is: one slide, and the layers open over it, outermost first. */
export interface DeckLocation {
  slide: string
  layers: string[]
}

export interface DeckSlide {
  id: string
  title: string
}

/** The figure an ask is about. dataset/row/col address the cell (row and column indexes, as data-pt-cell); the rest is display text. */
export interface DeckFigure {
  dataset: string
  row: number
  col: number
  rowLabel: string
  colLabel: string
  text: string
}

export type DeckAskKind = 'slide' | 'layer' | 'figure'

export interface DeckAsk {
  at: DeckLocation
  kind: DeckAskKind
  /** Display titles from the deck down to what was asked about. */
  path: string[]
  figure?: DeckFigure
  /** The starting question nearest the ask on the page (innermost layer, outer layers, then the slide); absent when none is set. */
  question?: string
}

export type DeckMessage =
  | { type: 'ready'; title: string; slides: DeckSlide[] }
  | { type: 'location'; at: DeckLocation }
  | { type: 'ask'; ask: DeckAsk }

export type HostMessage =
  | { source: typeof HOST_SOURCE; v: typeof FRAME_VERSION; type: 'ask-state'; enabled: boolean; reason?: string }
  | { source: typeof HOST_SOURCE; v: typeof FRAME_VERSION; type: 'go'; at: string }

/** Whether the framing page lets the person ask, and why not. */
export interface PresentationAskState {
  enabled: boolean
  reason?: string
}

/** A display label: a string, control characters out, whitespace collapsed, capped with an ellipsis. Anything else is ''. */
export function cleanLabel(value: unknown, max = TITLE_MAX): string {
  if (typeof value !== 'string') return ''
  // eslint-disable-next-line no-control-regex
  const s = value.replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, ' ').replace(/\s+/g, ' ').trim()
  return s.length <= max ? s : s.slice(0, max - 1).trimEnd() + '…'
}

/**
 * Reads a deck location: `#/s` or `#/s/l`, where l is the innermost open
 * layer. Layer ids are unique in a page, so the deck opens a nested layer's
 * outer layers itself and its routes never have more parts (deck.js
 * parseRoute). The `?at=` form without the leading `#/` (`s/l`) is accepted
 * too. Null for anything else, an id that breaks the rule included.
 */
export function parseDeckAt(value: unknown): DeckLocation | null {
  if (typeof value !== 'string') return null
  const body = value.startsWith('#/') ? value.slice(2) : value.startsWith('/') ? value.slice(1) : value
  if (!body || body.length > 300) return null
  const parts = body.split('/')
  if (parts.length > 2) return null
  if (!parts.every(p => DECK_ID_RE.test(p))) return null
  return { slide: parts[0], layers: parts.slice(1) }
}

/** `#/s` or `#/s/l` (the innermost layer), the hash a deck routes on. */
export function formatDeckAt(at: DeckLocation): string {
  return `#/${formatDeckAtParam(at)}`
}

/** `s` or `s/l`, the `?at=` form for a dashboard URL. */
export function formatDeckAtParam(at: DeckLocation): string {
  const layer = innermostLayer(at)
  return layer ? `${at.slide}/${layer}` : at.slide
}

/** The innermost layer open, or undefined on the slide itself. */
export function innermostLayer(at: DeckLocation): string | undefined {
  return at.layers.length ? at.layers[at.layers.length - 1] : undefined
}

function readSlides(value: unknown): DeckSlide[] | null {
  if (!Array.isArray(value)) return null
  const out: DeckSlide[] = []
  for (const s of value.slice(0, MAX_SLIDES)) {
    if (!s || typeof s !== 'object') return null
    const { id, title } = s as { id?: unknown; title?: unknown }
    if (typeof id !== 'string' || !DECK_ID_RE.test(id)) return null
    out.push({ id, title: cleanLabel(title) })
  }
  return out
}

/** A cell index: a whole number from 0 to the seed route's bound. */
export function isCellIndex(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= DECK_MAX_CELL_INDEX
}

function readFigure(value: unknown): DeckFigure | null {
  if (!value || typeof value !== 'object') return null
  const f = value as Record<string, unknown>
  if (typeof f.dataset !== 'string' || !DECK_DATASET_RE.test(f.dataset)) return null
  if (!isCellIndex(f.row) || !isCellIndex(f.col)) return null
  return {
    dataset: f.dataset,
    row: f.row,
    col: f.col,
    rowLabel: cleanLabel(f.rowLabel),
    colLabel: cleanLabel(f.colLabel),
    text: cleanLabel(f.text, FIGURE_TEXT_MAX),
  }
}

/** A starting question, on one line, cut to DECK_QUESTION_MAX; '' for a non-string or blank one. */
export function cleanQuestion(value: unknown): string {
  return cleanLabel(value, Number.MAX_SAFE_INTEGER).slice(0, DECK_QUESTION_MAX).trimEnd()
}

function readAsk(m: Record<string, unknown>): DeckAsk | null {
  const at = parseDeckAt(m.at)
  if (!at) return null
  const kind = m.kind
  if (kind !== 'slide' && kind !== 'layer' && kind !== 'figure') return null
  if (kind === 'layer' && at.layers.length === 0) return null
  const path = Array.isArray(m.path)
    ? m.path.slice(0, 2 + DECK_MAX_LAYERS).map(t => cleanLabel(t)).filter(Boolean)
    : []
  // A bad question is dropped; the ask still stands.
  const question = cleanQuestion(m.question)
  const asked = question ? { question } : {}
  if (kind !== 'figure') return { at, kind, path, ...asked }
  const figure = readFigure(m.figure)
  if (!figure) return null
  return { at, kind, path, figure, ...asked }
}

/** Reads a message's payload, or null when it is not a valid version 1 deck message. Checks no origin. */
export function parseDeckMessage(data: unknown): DeckMessage | null {
  if (!data || typeof data !== 'object') return null
  const m = data as Record<string, unknown>
  if (m.source !== DECK_SOURCE || m.v !== FRAME_VERSION) return null
  switch (m.type) {
    case 'ready': {
      const slides = readSlides(m.slides)
      if (!slides) return null
      return { type: 'ready', title: cleanLabel(m.title), slides }
    }
    case 'location': {
      const at = parseDeckAt(m.at)
      return at ? { type: 'location', at } : null
    }
    case 'ask': {
      const ask = readAsk(m)
      return ask ? { type: 'ask', ask } : null
    }
    default:
      return null
  }
}

/**
 * A deck message, only when it came from `frame` (the framed iframe's own
 * window) and from `origin` (this page's). Every other window, a nested frame
 * or a popup included, is ignored.
 */
export function readDeckMessage(
  event: Pick<MessageEvent, 'data' | 'origin' | 'source'>,
  frame: Window | null | undefined,
  origin: string = window.location.origin,
): DeckMessage | null {
  if (!frame || event.source !== frame) return null
  if (event.origin !== origin) return null
  return parseDeckMessage(event.data)
}

export function askStateMessage(state: PresentationAskState): HostMessage {
  return state.enabled
    ? { source: HOST_SOURCE, v: FRAME_VERSION, type: 'ask-state', enabled: true }
    : { source: HOST_SOURCE, v: FRAME_VERSION, type: 'ask-state', enabled: false, ...(state.reason ? { reason: state.reason } : {}) }
}

export function goMessage(at: DeckLocation): HostMessage {
  return { source: HOST_SOURCE, v: FRAME_VERSION, type: 'go', at: formatDeckAt(at) }
}

/** Posts to the framed page, to this origin only. */
export function postToDeck(frame: Window | null | undefined, message: HostMessage, origin: string = window.location.origin): void {
  if (!frame) return
  try {
    frame.postMessage(message, origin)
  } catch {
    // The frame navigated away or is closing: nothing to tell.
  }
}

/** "Deck › Slide › Layer", the breadcrumb of an ask. */
export function askBreadcrumb(ask: DeckAsk): string {
  return ask.path.join(' › ')
}

/**
 * The composer text "Ask in this chat" writes: what was asked about, and the
 * page link the agent can open (`data/index.html#/s/l`), then the slide's or
 * layer's starting question when it has one. The person edits it or adds
 * their own; nothing is sent.
 */
export function askDraft(ask: DeckAsk, entryPath: string): string {
  const where = askBreadcrumb(ask) || formatDeckAt(ask.at)
  const link = `${entryPath}${formatDeckAt(ask.at)}`
  const question = ask.question ?? ''
  const figure = ask.figure
  if (figure) {
    const cell = [figure.rowLabel || `row ${figure.row}`, figure.colLabel || `column ${figure.col}`].join(' × ')
    const value = figure.text ? `${figure.text} ` : ''
    return `About the figure ${value}(${cell}, dataset ${figure.dataset}) on “${where}” (${link}): ${question}`
  }
  return `About “${where}” (${link}): ${question}`
}
